// @ts-ignore
import MoveComponent from "../../../core/gd-components/base-component/move-component"
import DndManager from "./managers/dnd-manager"
import {DndType} from "./@types";

export type DNDComponentProps = {
    data?                       : any         // some component data

    dndType?                    : DndType //'target' | 'source' | 'both' | 'disabled'
    dndManager?                 : DndManager

    dndZIndex?                  : number
    dragTriggerDelta?           : number

    disableMouseMoveListeners?  : boolean // from MoveComponent
    disableMouseEnterListeners? : boolean // from HoverComponent

    onClick?                    : func

    //target
    onDraggedOver?              : func
    onDrop?                     : func
}

export default class DNDComponent<T extends DNDComponentProps> extends MoveComponent<T> {

    dragTriggerDelta: number

    mouseDownEvent  : any
    dragStarted     : boolean = false

    static defaultProps = {
        dndType             : 'both',
        dndZIndex           : 0,
        dragTriggerDelta    : 0,
    }

    constructor(props: DNDComponentProps) {
        super(props);

        // components do not inherit defaultProps
        this.dragTriggerDelta = !_.isNil(this.props.dragTriggerDelta) ? this.props.dragTriggerDelta : 0;

        this._bind('getDraggedOverPreviewType', 'isDraggedItem', 'canDrag', 'canDrop', 'onDragStart', 'onDrag', 'onDragEnd',
            'onDraggedOver', 'onDraggedOut', 'onDrop', 'getInitialRect', 'onClick');
    }

    componentDidMount() {
        super.componentDidMount();
        const { dndType, dndManager } = this.props;
        if (dndManager && ['target','both'].includes(dndType!))
            dndManager.addDropTarget(this);

        if (dndManager && ['source','both'].includes(dndType!))
            dndManager.addDragSource(this);
    }

    componentWillUnmount() {
        super.componentWillUnmount();
        const { dndType, dndManager } = this.props;
        if (dndManager && ['target','both'].includes(dndType!))
            dndManager.removeDropTarget(this);

        if (dndManager && ['source','both'].includes(dndType!))
            dndManager.removeDragSource(this);

        this._safariFixTextCursorWhileDragging(true);
    }

    getDNDId = () => this.props.data?.id;

    //for target only to determine dragged item preview type while dragged over
    getDraggedOverPreviewType() {
        return (this.props.dndManager && ['target','both'].includes(this.props.dndType!)) ? 'default' : null;
    }

    copyDraggedItemPositions(component: any) {
        const lmp = component.getLastMousePosition();
        this.setLastMousePosition({clientX: lmp.x, clientY: lmp.y});
        const sp = component.getStartPosition();
        this.setStartPosition(sp);
    }

    isDraggedItem(id: ID) {
        return this.props.dndManager && this.props.dndManager.isDraggedItem(id);
    }

    setPosition(x: number, y: number, width: number) {
        //do nothing
    }

    onMouseDown(e: MouseEvent, callback: func) {
        this._safariFixTextCursorWhileDragging(false);

        this.mouseDownEvent = e; // for onClick available when drag is not available
        this._addWindowListeners();

        this.setLastMousePosition(e);
        this.setStartPosition();

        // only main (left) mouse button is allowed
        if (e.button !== 0) return

        if(!this.canDrag(e)) return;
        // e.stopPropagation(); // used to prevent propagation to gantt/workload scroll drag feature while dnd is in progress, but it caused popover onClickOutside not working
        // e.preventDefault(); // should be commented out for hours allocated input to receive focus

        this.dragStarted = true;
        if (this.props.dndManager) this.props.dndManager.setDragStarted(true);
    }

    onMouseMove(e: MouseEvent, callback: func) {
        const delta: any = this.getMousePositionDelta(e);

        if (['source','both'].includes(this.props.dndType!) && this.mouseDownEvent && (Math.abs(delta.dx) > this.dragTriggerDelta || Math.abs(delta.dy) > this.dragTriggerDelta)) {
            if (this.dragStarted && this.props.dndManager) {
                this.props.dndManager.dragStart(this, this.mouseDownEvent);
            }
            this.mouseDownEvent = null; //drag started then no onClick event
        }
    }

    onMouseUp(e: any, callback: func) {
        this._safariFixTextCursorWhileDragging(true);

        if (this.mouseDownEvent) {
            if (this.props.dndManager) this.props.dndManager.setDragStarted(false);
            this.onClick(e);
        }

        this._removeWindowListeners();
        this.mouseDownEvent = null;
        this.dragStarted = false;
    }

    /*onMouseEnter(e, callback) {
        super.onMouseEnter(e, callback);
        this._safariFixTextCursorWhileDragging(false);
    }

    onMouseLeave(e, callback) {
        super.onMouseLeave(e, callback);

        if (!this.isDraggedItem(this.getDNDId()))
            this._safariFixTextCursorWhileDragging(true);
    }*/

    onMouseLeave(e?: MouseEvent, callback?: callback) {
        super.onMouseLeave(e, callback)

        this.mouseDownEvent = null //cursor left component borders then no onClick event
    }

    _safariFixTextCursorWhileDragging = (value: any) => {document.onselectstart = () => value}

    canDrag(e: MouseEvent) {
        return ['source','both'].includes(this.props.dndType!);
    }

    canDrop(draggedItem: any) {return ['target','both'].includes(this.props.dndType!)}

    onDragStart(): any { // is triggered on this item is started dragging
        this.addClass('is-dragged');
        this.onMouseLeave();
        return null;
    }

    onDrag(dx: number,dy: number) {/*should be overridden if necessary*/}                                       // is triggered when this item is dragged
    onDragEnd(mouseEvent: MouseEvent, dx: number, dy: number, dropResult: any) {this.removeClass('is-dragged')}         // is triggered when this item is stopped being dragged
    onDraggedOver(draggedItem: any, mouseEvent: MouseEvent) {this.addClass('is-dragged-over')}          // is triggered when dragged item is over
    onDraggingOver(draggedItem: any, mouseEvent: MouseEvent) {/*should be overridden if necessary*/}             // is triggered on each mouse move while dragged item is moved over this item
    onDraggedOut(draggedItem: any, isOnDrop: boolean) {this.removeClass('is-dragged-over')}          // is triggered when dragged item is not over any more, isOnDrop - if is called on drop
    onDrop(draggedItem: any, dropResult: any) {return dropResult; /*should be overridden if necessary*/}  // is triggered when dragged item is dropped to this item
    onClick(e: MouseEvent) {/*should be overridden if necessary*/}                                          // special click event because of mouseup and mousedown events are modified and used for DND

    getInitialRect() {
        const rect = this.componentEl.getBoundingClientRect();
        return {
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height
        }
    }
}